import { useCallback, useMemo, useRef, useState } from 'react';
import {
  AnimatedPopover,
  Content,
  Icon,
  Inline,
  InlineItem,
  LabelText,
  MenuItem,
  MonthPicker,
  Stack,
  Text,
  useEventListener,
  useOnClickOutside,
} from '@kinesis/bungle';
import { YearEndingPeriod } from '@/types';
import {
  disclosureMonthFromIndex,
  disclosureMonthIndex,
  renderDisclosurePeriod,
} from '@/data/disclosure-month';

// Year-ending-period editor: a config row (calendar icon + period text or
// "Configure period" placeholder) that opens an AnimatedPopover over a
// MonthPicker. Shared by the disclosure header (the disclosure period) and
// the pathway sidebar (the base period) — the same 12-months-ending-at
// concept; `description` carries each surface's own explanation.
// Extracted from `disclosure-header` when the second consumer landed.
const PeriodMenuItem = (props: PeriodMenuItemProps) => {
  const { period, onChange, description } = props;
  const ref = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const toggle = useCallback(() => setIsOpen((v) => !v), []);
  const close = useCallback(() => setIsOpen(false), []);

  const date = useMemo(
    () =>
      period
        ? new Date(period.year, disclosureMonthIndex(period.month))
        : undefined,
    [period],
  );

  const handlePick = useCallback(
    (picked: Date) => {
      onChange({
        year: picked.getFullYear(),
        month: disclosureMonthFromIndex(picked.getMonth()),
      });
      close();
    },
    [onChange, close],
  );

  useOnClickOutside(
    useMemo(() => [ref, popoverRef], []),
    useCallback(() => close(), [close]),
  );

  useEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      close();
    }
  });

  return (
    <>
      <MenuItem
        ref={ref}
        icon={<Icon magnitude='medium' type='calendar' />}
        content={
          period ? (
            <Text>{renderDisclosurePeriod(period)}</Text>
          ) : (
            <Text tone='secondary'>Configure period</Text>
          )
        }
        onClick={toggle}
      />
      {/* Left-edge aligned with its own width: the anchor row can be a
          narrow sidebar item (the pathway base period), and a popover
          stretched or pinned to that box overflows its two-column
          content. */}
      <AnimatedPopover
        stopPropagation
        justify='start'
        open={isOpen}
        offset={4}
        placement='bottom'
        preventOverflow
        targetRef={ref}
      >
        <Content
          ref={popoverRef}
          borderRadiusSize='large'
          border
          shadow
          background='white'
          padding='medium'
          width={520}
        >
          <Inline alignY='top' space='large'>
            <InlineItem sizing='fill-container'>
              <Content padding='small'>
                <Stack space='medium'>
                  <LabelText>Period end</LabelText>
                  <Text tone='secondary'>{description}</Text>
                </Stack>
              </Content>
            </InlineItem>
            <InlineItem sizing='fill-container'>
              <MonthPicker value={date} onChange={handlePick} />
            </InlineItem>
          </Inline>
        </Content>
      </AnimatedPopover>
    </>
  );
};

export type PeriodMenuItemProps = {
  period: YearEndingPeriod | undefined;
  onChange: (v: YearEndingPeriod) => void;
  // The surface's own explanation of what the picked end month means,
  // shown beside the picker.
  description: string;
};

export { PeriodMenuItem };
